import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { MARKET_EXCLUDED_CATEGORIES } from "../../shared/market";
import { useI18n } from "../i18n/I18nProvider";
import { api, imageUrl } from "../lib/api";
import { useCatalog } from "../lib/CatalogProvider";
import { useAsync } from "../lib/useAsync";
import { marketApi, type CatalogCard } from "./api";
import { useGame } from "../lib/GameProvider";

const MIN_CHARS = 2;
const DEBOUNCE_MS = 200;
const PAGE = 24;

const detail = (c: CatalogCard) => [c.setName, c.cardNumber ? `#${c.cardNumber}` : null, c.rarity].filter(Boolean).join(" · ");

/** The kind ("singles", "booster-box"…) and set a search is narrowed to; "" for any. */
interface Narrow {
  kind: string;
  set: string;
}

/**
 * Finds a catalog card or product (listings and "La busco"): suggestions with thumbnails while typing;
 * Enter or "Buscar" opens every result as a grid of images to pick from. With `filters`, a kind
 * (cards, booster boxes…) and a set narrow it, and either one alone browses without typing.
 */
export function CardPicker({ onPick, label, filters = false }: { onPick: (card: CatalogCard) => void; label?: string; filters?: boolean }) {
  const { t, locale } = useI18n();
  const { catalog } = useCatalog();
  const { game } = useGame();
  const [narrow, setNarrow] = useState<Narrow>({ kind: "", set: "" });
  const kinds = catalog.categories.filter((c) => !MARKET_EXCLUDED_CATEGORIES.includes(c.slug));
  const sets = useAsync(
    (signal) => (filters ? api.facets({ games: [game], categories: narrow.kind ? [narrow.kind] : [], inStock: false, sets: [] }, signal) : Promise.resolve(null)),
    [filters, narrow.kind, game],
  );
  const setNames = (sets.data?.sets ?? []).map((x) => x.name);
  const setOptions = narrow.set && !setNames.includes(narrow.set) ? [narrow.set, ...setNames] : setNames;
  const filtered = Boolean(narrow.kind || narrow.set);
  const by = (n: Narrow) => ({ category: n.kind || undefined, set: n.set || undefined });
  const id = useId();
  const listId = `${id}-list`;
  const optionId = (i: number) => `${id}-opt-${i}`;
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState<CatalogCard[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [grid, setGrid] = useState<{ q: string; label: string; items: CatalogCard[]; total: number; fuzzy: boolean; page: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const focused = useRef(false);

  // Suggestions while typing; a late answer only updates them (it never reopens the list).
  useEffect(() => {
    const text = q.trim();
    if (text.length < MIN_CHARS) {
      setSuggestions([]);
      return;
    }
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => {
      marketApi
        .cards(text, { size: 8, ...by(narrow) }, ctrl.signal)
        .then((r) => {
          if (ctrl.signal.aborted) return;
          setSuggestions(r.items);
          setActive(-1);
        })
        .catch(() => {});
    }, DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      ctrl.abort();
    };
  }, [q, narrow.kind, narrow.set]);

  const pick = (c: CatalogCard) => {
    onPick(c);
    setQ("");
    setSuggestions([]);
    setOpen(false);
    setGrid(null);
  };

  /** Every result, as a grid (page 1, or the next page appended). */
  const search = async (page = 1, text = q.trim(), n = narrow) => {
    if (text.length < MIN_CHARS && !n.kind && !n.set) return;
    setOpen(false);
    setLoading(true);
    setError(false);
    const kindName = kinds.find((k) => k.slug === n.kind)?.name[locale];
    try {
      const r = await marketApi.cards(text, { size: PAGE, page, game, ...by(n) });
      setGrid((g) => ({ q: text, label: [text, kindName, n.set].filter(Boolean).join(" · "), items: page > 1 && g ? [...g.items, ...r.items] : r.items, total: r.total, fuzzy: r.fuzzy, page }));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  /** A new kind or set: the results on screen follow it. */
  const change = (n: Narrow) => {
    setNarrow(n);
    if (grid) void search(1, grid.q, n);
  };

  const shown = open && suggestions.length > 0;
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" && suggestions.length) {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (a + 1) % suggestions.length);
    } else if (e.key === "ArrowUp" && shown) {
      e.preventDefault();
      setActive((a) => (a <= 0 ? suggestions.length - 1 : a - 1));
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      setOpen(false);
      setActive(-1);
    } else if (e.key === "Enter") {
      // Never submits a surrounding form: picks the highlighted suggestion, or shows every result.
      e.preventDefault();
      if (shown && active >= 0 && suggestions[active]) pick(suggestions[active]);
      else void search();
    }
  };

  return (
    <div className="card-picker">
      <label htmlFor={id}>{label ?? t("mkt.findCard")}</label>
      <div className="card-picker-box">
        <input
          id={id}
          type="search"
          value={q}
          placeholder={t("mkt.findCardHint")}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={shown}
          aria-controls={listId}
          aria-activedescendant={shown && active >= 0 ? optionId(active) : undefined}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            focused.current = true;
            if (suggestions.length) setOpen(true);
          }}
          onBlur={() => {
            focused.current = false;
            setOpen(false);
          }}
          onKeyDown={onKeyDown}
        />
        <button type="button" className="btn btn-primary" disabled={(q.trim().length < MIN_CHARS && !filtered) || loading} onClick={() => void search()}>
          {t("mkt.searchCards")}
        </button>
        <ul id={listId} role="listbox" aria-label={t("search.suggestions")} className="search-suggest card-suggest" hidden={!shown}>
          {suggestions.map((c, i) => (
            <li
              key={c.id}
              id={optionId(i)}
              role="option"
              aria-selected={i === active}
              className={i === active ? "search-option is-active" : "search-option"}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(c)}
            >
              <img src={imageUrl(c.imageKey)} alt="" width={40} height={40} loading="lazy" decoding="async" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
              <span className="search-option-text">
                <span className="search-option-name">{c.name}</span>
                <span className="search-option-detail">{detail(c)}</span>
              </span>
            </li>
          ))}
          <li role="option" aria-selected={false} className="search-option search-all" onMouseDown={(e) => e.preventDefault()} onClick={() => void search()}>
            {t("mkt.seeAllCards", { q: q.trim() })}
          </li>
        </ul>
      </div>

      {filters && (
        <div className="field-row">
          <div className="field">
            <label htmlFor={`${id}-kind`}>{t("mkt.pickKind")}</label>
            <select id={`${id}-kind`} value={narrow.kind} onChange={(e) => change({ ...narrow, kind: e.target.value })}>
              <option value="">{t("mkt.pickKindAll")}</option>
              {kinds.map((k) => (
                <option key={k.slug} value={k.slug}>
                  {k.name[locale]}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor={`${id}-set`}>{t("mkt.pickSet")}</label>
            <select id={`${id}-set`} value={narrow.set} onChange={(e) => change({ ...narrow, set: e.target.value })}>
              <option value="">{t("mkt.pickSetAll")}</option>
              {setOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {loading && !grid && <p className="muted small">{t("loading")}</p>}
      {error && <p className="form-error">{t("error.generic")}</p>}
      {grid && (
        <section className="card-grid-results" aria-label={t("mkt.cardResults", { count: grid.total, q: grid.label })}>
          <div className="card-grid-head">
            <p className="small">
              {grid.total === 0 ? t("mkt.noCardFound") : grid.fuzzy ? t("mkt.cardResultsFuzzy", { q: grid.label, count: grid.total }) : t("mkt.cardResults", { count: grid.total, q: grid.label })}
            </p>
            <button type="button" className="btn btn-link btn-sm" onClick={() => setGrid(null)}>
              {t("mkt.closeResults")}
            </button>
          </div>
          <ul className="card-grid">
            {grid.items.map((c) => (
              <li key={c.id}>
                <button type="button" onClick={() => pick(c)}>
                  <img src={imageUrl(c.imageKey)} alt="" width={160} height={224} loading="lazy" decoding="async" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
                  <strong>{c.name}</strong>
                  <span className="muted small">{detail(c)}</span>
                </button>
              </li>
            ))}
          </ul>
          {grid.items.length < grid.total && (
            <button type="button" className="btn btn-outline" disabled={loading} onClick={() => void search(grid.page + 1, grid.q)}>
              {loading ? t("loading") : t("mkt.moreCards")}
            </button>
          )}
        </section>
      )}
    </div>
  );
}
